import { Injectable } from "@angular/core";
import { AbstractControl, FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { AppBarActionsService, EnumHelper, ProductName } from "common";
import { BankAccountData, FacilityDetailsData, FacilityEditData, SecCode } from "../../domain/models/facility.model";

const DEFAULT_SEC_CODE = SecCode.PPD;
const DEFAULT_COMPANY_ENTRY_DESCRIPTION = 'ePay';

@Injectable()
export class FacilityDetailsFacade {
  readonly productTypeOptions = EnumHelper.getNamesAndValues(ProductName);

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly appBarActionsService: AppBarActionsService
  ) {}

  initForm() : UntypedFormGroup {
    return this.formBuilder.group({
      name: [undefined, Validators.required],
      isDefault: [false],
      allowedProducts: [this.productTypeOptions.map(opt => opt.value), Validators.required],
      maximumAcceptableLoanCreditLimit: [undefined, this.getAmountValidators()],
      borrowingCapacity: [undefined, this.getAmountValidators()],
      active: [true, Validators.required],
      
      outboundBankAccount: this.formBuilder.group({
        bankName: [undefined, this.getBankNameValidators()],
        routingNumber: [undefined, this.getRoutingNumberValidators()],
        accountNumber: [undefined, this.getAccountNumberValidators()],
        bankNachaLocation: [undefined, this.getBankNachaLocationValidators()],
        companyAchCode: [undefined, this.getCompanyAchCodeValidators()],
        secCode: [DEFAULT_SEC_CODE, this.getSecCodeValidators()],
        companyEntryDescription: [DEFAULT_COMPANY_ENTRY_DESCRIPTION, this.getCompanyEntryDescriptionValidators()],
        nachaDestinationName: [undefined, this.getNachaDestinationNameValidators()]
      }),

      inboundBankAccount: this.formBuilder.group({
        sameAsOutbound: [false],
        bankName: [undefined, this.getBankNameValidators()],
        routingNumber: [undefined, this.getRoutingNumberValidators()],
        accountNumber: [undefined, this.getAccountNumberValidators()],
        bankNachaLocation: [undefined, this.getBankNachaLocationValidators()],
        companyAchCode: [undefined, this.getCompanyAchCodeValidators()],
        secCode: [DEFAULT_SEC_CODE, this.getSecCodeValidators()],
        companyEntryDescription: [DEFAULT_COMPANY_ENTRY_DESCRIPTION, this.getCompanyEntryDescriptionValidators()],
        nachaDestinationName: [undefined, this.getNachaDestinationNameValidators()]
      }),
      
      seniorLenders: this.formBuilder.group({
        percentage: [undefined, this.getLendersPercentageValidators()],
        lendersNames: this.formBuilder.array([], validateAtLeastOneLender())
      }),
      
      mezzanineLenders: this.formBuilder.group({
        percentage: [undefined, [Validators.min(0.01), Validators.max(100)]],
        lendersNames: this.formBuilder.array([])
      })
    });
  }

  createLender(name: string = ''): FormGroup {
    return this.formBuilder.group({
      lenderName: [name, Validators.required]
    });
  }

  patchFormFromFacility(form: UntypedFormGroup, facility: FacilityDetailsData): void {
    if (!form || !facility) return;

    form.patchValue({
      name: facility.name,
      isDefault: facility.isDefault,
      allowedProducts: facility.allowedProducts || [],
      maximumAcceptableLoanCreditLimit: facility.maximumAcceptableLoanCreditLimit,
      borrowingCapacity: facility.borrowingCapacity,
      active: facility.active,
      outboundBankAccount: this.toBankAccountFormValue(facility.outboundBankAccount),
      inboundBankAccount: {
        ...this.toBankAccountFormValue(facility.inboundBankAccount),
        sameAsOutbound: false
      },
      seniorLenders: {
        percentage: facility.seniorLenders?.percentage ?? null
      },
      mezzanineLenders: {
        percentage: facility.mezzanineLenders?.percentage ?? null
      }
    });

    const seniorArray = form.get('seniorLenders.lendersNames') as UntypedFormArray;
    seniorArray.clear();
    (facility.seniorLenders?.lendersNames || []).forEach(name => {
      seniorArray.push(this.createLender(name));
    });

    const mezzArray = form.get('mezzanineLenders.lendersNames') as UntypedFormArray;
    mezzArray.clear();
    (facility.mezzanineLenders?.lendersNames || []).forEach(name => {
      mezzArray.push(this.createLender(name));
    });

    this.toggleDefaultFacilityMode(form, facility.isDefault ?? false);
  }

  formToFacilityData(form: UntypedFormGroup, onlyDirty?: boolean ): FacilityEditData {
    const dirty = !!onlyDirty;
    const fullValue = form.getRawValue();
    const result: FacilityEditData = {};

    const addIfDirty = (path: string, value: any): void => {
      if (!dirty || form.get(path)?.dirty) {
        (result as any)[path as keyof FacilityEditData] = value;
      }
    };

    ['name', 'isDefault', 'allowedProducts', 'maximumAcceptableLoanCreditLimit', 'borrowingCapacity', 'active']
      .forEach(key => addIfDirty(key, fullValue[key]));

    if (!dirty || form.get('outboundBankAccount')?.dirty) {
      result.outboundBankAccount = { ...fullValue.outboundBankAccount };
    }

    if (!dirty || form.get('inboundBankAccount')?.dirty) {
      const { sameAsOutbound, ...cleanInbound } = fullValue.inboundBankAccount;

      if(sameAsOutbound) {
        result.inboundBankAccount = { ...fullValue.outboundBankAccount };
      }
      else {
        result.inboundBankAccount = cleanInbound;
      }
    }

    const seniorArray = form.get('seniorLenders.lendersNames') as UntypedFormArray;
    const mezzArray = form.get('mezzanineLenders.lendersNames') as UntypedFormArray;

    if (!onlyDirty || form.get('seniorLenders')?.dirty) {
      result.seniorLenders = {
        percentage: fullValue.seniorLenders.percentage,
        lendersNames: this.getLenderNames(seniorArray)
      };
    }

    if (!onlyDirty || form.get('mezzanineLenders')?.dirty) {
      result.mezzanineLenders = {
        percentage: fullValue.mezzanineLenders.percentage,
        lendersNames: this.getLenderNames(mezzArray)
      };
    }

    return result;
  }

  toggleDefaultFacilityMode(form: UntypedFormGroup, isDefault: boolean): void {
    const required = !isDefault;
    this.toggleValidator(form, 'borrowingCapacity', required, this.getAmountValidators());
    this.toggleValidator(form, 'maximumAcceptableLoanCreditLimit', required, this.getAmountValidators());
    this.toggleValidator(form, 'seniorLenders.percentage', required, [Validators.required, Validators.min(0.01), Validators.max(100)]);

    const lendersArray = form.get('seniorLenders.lendersNames') as UntypedFormArray;
    lendersArray.clearValidators();
    if (required) {
      lendersArray.setValidators(validateAtLeastOneLender());
    }
    lendersArray.updateValueAndValidity({ emitEvent: false });

    const fieldsToToggle = [
      'allowedProducts',
      'maximumAcceptableLoanCreditLimit',
      'borrowingCapacity',
      'active'
    ];

    fieldsToToggle.forEach(field => {
      const control = form.get(field);
      if (control) {
        isDefault ? control.disable({ emitEvent: false }) : control.enable({ emitEvent: false });
      }
    });

    if (isDefault) {
      form.get('maximumAcceptableLoanCreditLimit').patchValue(null);
      form.get('maximumAcceptableLoanCreditLimit').markAsDirty();

      form.get('borrowingCapacity').patchValue(null);
      form.get('borrowingCapacity').markAsDirty();

      form.get('seniorLenders.percentage').patchValue(null);
      form.get('seniorLenders.percentage').markAsDirty();

      (form.get('seniorLenders.lendersNames') as UntypedFormArray).clear();
      form.get('seniorLenders.lendersNames').markAsDirty();

      (form.get('mezzanineLenders.lendersNames') as UntypedFormArray).clear();
      form.get('mezzanineLenders.lendersNames').markAsDirty();

      form.get('mezzanineLenders.percentage').patchValue(null);
      form.get('mezzanineLenders.percentage').markAsDirty();
    }
  }

  toggleInboundAccountSync(form: UntypedFormGroup, sameAsOutbound: boolean): void {
    const inbound = form.controls.inboundBankAccount;

    const fields = ['bankName', 'routingNumber', 'accountNumber', 'bankNachaLocation', 'companyAchCode', 'secCode', 'companyEntryDescription', 'nachaDestinationName'];

    if (sameAsOutbound) {
      inbound.clearValidators();
      fields.forEach(field => {
        const fieldControl = inbound.get(field);
        fieldControl.clearValidators();
        fieldControl.setValue(undefined);
      });
    } else {
      inbound?.enable({ emitEvent: false });     
      const inboundForm = inbound as UntypedFormGroup;

      inboundForm?.controls.bankName?.setValidators(this.getBankNameValidators());
      inboundForm?.controls.routingNumber?.setValidators(this.getRoutingNumberValidators());
      inboundForm?.controls.accountNumber?.setValidators(this.getAccountNumberValidators());
      inboundForm?.controls.bankNachaLocation?.setValidators(this.getBankNachaLocationValidators());
      inboundForm?.controls.companyAchCode?.setValidators(this.getCompanyAchCodeValidators());
      inboundForm?.controls.secCode?.setValidators(this.getSecCodeValidators());
      inboundForm?.controls.companyEntryDescription?.setValidators(this.getCompanyEntryDescriptionValidators());
      inboundForm?.controls.nachaDestinationName?.setValidators(this.getNachaDestinationNameValidators());
      inboundForm?.patchValue({
        secCode: inboundForm.controls.secCode?.value || DEFAULT_SEC_CODE,
        companyEntryDescription: inboundForm.controls.companyEntryDescription?.value || DEFAULT_COMPANY_ENTRY_DESCRIPTION
      }, { emitEvent: false });
       fields.forEach(field => {
        inbound.get(field)?.enable({ emitEvent: false });
      });
    }
  }

  changeMezzanineLendersNamesRequirement(form: UntypedFormGroup, percentage: number): void {
    const lendersArray = form.get('mezzanineLenders.lendersNames') as UntypedFormArray;
    lendersArray.clearValidators();
    if (percentage > 0) {
      lendersArray.setValidators(validateAtLeastOneLender());
    }
    lendersArray.updateValueAndValidity({ emitEvent: false });
  }

  changeMezzanineLendersPercentageRequirement(form: UntypedFormGroup, lendersNames: UntypedFormArray): void {
    const lendersPercentage = form.get('mezzanineLenders.percentage') as UntypedFormControl;
    lendersPercentage.clearValidators();
    if (lendersNames?.length > 0) {
      lendersPercentage.setValidators(this.getLendersPercentageValidators());
    }
    lendersPercentage.updateValueAndValidity({ emitEvent: false });
  }

  initAppBarActions() : void {
    this.appBarActionsService.actions = [
      { id: "delete", label: "Delete", buttonType: "button" },
      { id: "cancel", label: "Cancel", buttonType: "button" },
      {
        id: "save",
        label: "Save",
        buttonType: "submit",
        buttonAppearance: "flat",
        buttonColor: "primary",
        disabled: true,
      },
    ];
  }

  private getLendersPercentageValidators(): ValidatorFn[] {
    return [Validators.required, Validators.min(0.01), Validators.max(100)];
  }

  private getRoutingNumberValidators(): ValidatorFn[] {
    return [Validators.required, Validators.minLength(9), Validators.maxLength(9)];
  }

  private getAccountNumberValidators(): ValidatorFn[] {
    return [Validators.required, Validators.maxLength(128)];
  }

  private getBankNameValidators(): ValidatorFn[] {
    return [Validators.required, Validators.maxLength(128)];
  }

  private getBankNachaLocationValidators(): ValidatorFn[] {
    return [Validators.required, Validators.maxLength(2048)];
  }

  private getCompanyAchCodeValidators(): ValidatorFn[] {
    return [Validators.required, Validators.maxLength(10)];
  }

  private getSecCodeValidators(): ValidatorFn[] {
    return [Validators.required];
  }

  private getCompanyEntryDescriptionValidators(): ValidatorFn[] {
    return [Validators.required, Validators.maxLength(10)];
  }

  private getNachaDestinationNameValidators(): ValidatorFn[] {
    return [Validators.maxLength(23)];
  }

  private toBankAccountFormValue(account: BankAccountData): BankAccountData {
    return {
      ...account,
      secCode: account?.secCode ?? DEFAULT_SEC_CODE,
      companyEntryDescription: account?.companyEntryDescription ?? DEFAULT_COMPANY_ENTRY_DESCRIPTION
    };
  }

  private getAmountValidators(): ValidatorFn[] {
    return [Validators.required, Validators.min(0.01), Validators.max(9999999999.99)];
  }

  private toggleValidator(form: UntypedFormGroup, controlName: string, required: boolean, customValidators?: ValidatorFn | ValidatorFn[]): void {
    const control = form.get(controlName);
    if (!control) return;

    control.clearValidators();
    if (required) {
      control.setValidators(
        customValidators 
        ? customValidators 
        : Validators.required);
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  private getLenderNames(array: UntypedFormArray): string[] {
    return array.controls.map(c => c.get('lenderName')?.value).filter(Boolean);
  }

}

export function validateAtLeastOneLender(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const arr = control as UntypedFormArray;
    const hasAtLeastOne = arr.controls.length > 0;

    return hasAtLeastOne ? null : { required: true };
  };
}
